"use client";

import { useState, useRef } from "react";
import {
  X,
  Edit3,
  Menu,
  Settings,
  FileText,
  ImageIcon,
  Video,
} from "lucide-react";

export default function EditBusinessModal({
  open,
  setOpen,
  handleFormSubmit,
  isLoading,
  handleBusinessOfferSubmit,
  handleBusinessCatalogSubmit,
  handleDetailsSubmit,
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [catalogForm, setCatalogForm] = useState({
    category: "restaurant",
    items: [],
    catalogImage: null,
  });
  const [newItem, setNewItem] = useState({
    name: "",
    description: "",
    price: "",
  });

  const [offerForm, setOfferForm] = useState({
    type: "",
    description: "",
    percentage: "",
  });

  const [businessDetailsForm, setBusinessDetailsForm] = useState({
    contactemail: "",
    contactphone: "",
    contactotherphone: "",
  });

  const [additionalInfoForm, setAdditionalInfoForm] = useState({
    additionalInfo: "",
  });

  const [certificateFile, setCertificateFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const certificateInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const catalogImageInputRef = useRef(null);
  const handleOfferInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "percentage" && (value > 100 || value < 0)) {
      return;
    }
    setOfferForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleBusinessDetailsChange = (e) => {
    const { name, value } = e.target;
    setBusinessDetailsForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdditionalInfoChange = (e) => {
    const { name, value } = e.target;
    setAdditionalInfoForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCertificateFile(file);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
    }
  };

  const removeCertificate = () => {
    setCertificateFile(null);
    if (certificateInputRef.current) {
      certificateInputRef.current.value = "";
    }
  };

  const removeImage = () => {
    setImageFile(null);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const removeVideo = () => {
    setVideoFile(null);
    if (videoInputRef.current) {
      videoInputRef.current.value = "";
    }
  };

  const handleCatalogCategoryChange = (category) => {
    setCatalogForm((prev) => ({ ...prev, category, items: [] }));
    setNewItem({ name: "", description: "", price: "" });
  };

  const handleNewItemChange = (e) => {
    const { name, value } = e.target;
    setNewItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleCatalogMainImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCatalogForm((prev) => ({ ...prev, catalogImage: file }));
    }
  };

  const removeCatalogImage = () => {
    setCatalogForm((prev) => ({ ...prev, catalogImage: null }));
    if (catalogImageInputRef.current) {
      catalogImageInputRef.current.value = "";
    }
  };

  const addCatalogItem = () => {
    if (newItem.name.trim()) {
      setCatalogForm((prev) => ({
        ...prev,
        items: [...prev.items, { ...newItem, id: Date.now() }],
      }));
      setNewItem({ name: "", description: "", price: "" });
    }
  };

  const removeCatalogItem = (id) => {
    setCatalogForm((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const handleOfferSubmit = async () => {
    const result = await handleBusinessOfferSubmit(offerForm);
    if (result?.success) {
      setOpen(false);
      setSelectedOption(null);
      setOfferForm({ type: "", description: "", percentage: "" });
    }
  };

  const handleCatalogEdit = () => {
    setSelectedOption("catalog");
  };

  const handleBusinessDetailsEdit = () => {
    setSelectedOption("businessDetails");
  };

  const handleAdditionalInfoEdit = () => {
    setSelectedOption("additionalInfo");
  };

  const handleBusinessDetailsSubmit = async () => {
    const result =  await handleDetailsSubmit({
      ...businessDetailsForm,
      certificateFile,
      imageFile,
      videoFile}
    );
    if (result?.success) {
      setOpen(false);
      setSelectedOption(null);
    }
  };

  const handleCatalogSubmit = async () => {
    const result = await handleBusinessCatalogSubmit(catalogForm);
    if (result?.success) {
      setOpen(false);
      setSelectedOption(null);
      setCatalogForm({ category: "restaurant", items: [], catalogImage: null });
    }
  };

  const handleAdditionalInfoSubmit = async () => {
    const result = await handleFormSubmit(additionalInfoForm);
    if (result?.success) {
      setOpen(false);
      setSelectedOption(null);
      setAdditionalInfoForm({ additional_info: "" });
    }
  };

  const renderOfferForm = () => (
    <div className="p-4 sm:p-6">
      <h3 className="text-lg font-semibold text-[#232123] mb-4">Edit Offers</h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[#232123] mb-2">
            Offer Type
          </label>
          <input
            type="text"
            name="type"
            value={offerForm.type}
            onChange={handleOfferInputChange}
            placeholder="e.g., Flat discount, Buy one get one"
            className="w-full border border-[#e1e5e4] rounded-md p-3 text-sm text-[#232123] placeholder:text-[#9a999a] focus:outline-none focus:ring-1 focus:ring-[#7b189f]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#232123] mb-2">
            Description
          </label>
          <textarea
            name="description"
            value={offerForm.description}
            onChange={handleOfferInputChange}
            placeholder="Describe your offer in detail"
            className="w-full border border-[#e1e5e4] rounded-md p-3 min-h-[80px] text-sm text-[#232123] placeholder:text-[#9a999a] resize-none focus:outline-none focus:ring-1 focus:ring-[#7b189f]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-[#232123] mb-2">
            Percentage (%)
          </label>
          <input
            type="number"
            name="percentage"
            value={offerForm.percentage}
            onChange={handleOfferInputChange}
            placeholder="Enter discount percentage"
            min="0"
            max="100"
            className="w-full border border-[#e1e5e4] rounded-md p-3 text-sm text-[#232123] placeholder:text-[#9a999a] focus:outline-none focus:ring-1 focus:ring-[#7b189f]"
            onKeyPress={(e) => {
              if (e.key === "%") {
                e.preventDefault();
              }
            }}
          />
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t border-[#e1e5e4] mt-6">
        <button
          onClick={() => setSelectedOption(null)}
          className="text-[#232123] bg-transparent hover:bg-transparent font-normal text-base"
        >
          Back
        </button>
        <button
          onClick={handleOfferSubmit}
          className="bg-[#7b189f] hover:bg-[#7b189f] text-white px-6 sm:px-8 py-2 rounded-md"
        >
          {isLoading && (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
          {isLoading ? "Saving..." : "Save Offer"}{" "}
        </button>
      </div>
    </div>
  );

  const renderBusinessDetailsForm = () => (
    <div className="p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
      <h3 className="text-lg font-semibold text-[#232123] mb-4">
        Edit Business Details
      </h3>

      {/* Contact Information Section */}
      <div className="mb-6">
        <h4 className="text-base font-medium text-[#232123] mb-3">
          Contact Information
        </h4>
        <div className="space-y-4">
          <input
            name="contactemail"
            type="email"
            value={businessDetailsForm.contactemail}
            onChange={handleBusinessDetailsChange}
            placeholder="Contact Email*"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
          />
          <input
            name="contactphone"
            type="tel"
            value={businessDetailsForm.contactphone}
            onChange={handleBusinessDetailsChange}
            placeholder="Primary Phone Number*"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
          />
          <input
            name="contactotherphone"
            type="tel"
            value={businessDetailsForm.contactotherphone}
            onChange={handleBusinessDetailsChange}
            placeholder="Alternative Phone Number (Optional)"
            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
          />
        </div>
      </div>

      {/* Media Upload Section */}
      <div className="space-y-6">
        <h4 className="text-base font-medium text-[#232123] mb-3">
          Business Media
        </h4>

        {/* Business Certificate */}
        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS CERTIFICATE (OPTIONAL)
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              ref={certificateInputRef}
              onChange={handleCertificateChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />

            {certificateFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <FileText size={20} className="text-blue-500 mr-3" />
                  <div>
                    <div className="font-medium text-sm">
                      {certificateFile.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {(certificateFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeCertificate}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <FileText size={32} className="mx-auto text-gray-400 mb-2" />
                <button
                  onClick={() => certificateInputRef.current?.click()}
                  className="text-[#7B189F] hover:text-[#671485] font-medium text-sm"
                >
                  Upload Business Certificate
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  PDF, DOC, DOCX, JPG, PNG up to 10MB
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Business Image */}
        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS IMAGE (OPTIONAL)
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              ref={imageInputRef}
              onChange={handleImageChange}
              className="hidden"
              accept="image/*"
            />

            {imageFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg mr-3 overflow-hidden">
                    <img
                      src={URL.createObjectURL(imageFile) || "/placeholder.svg"}
                      alt="Business"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <div className="font-medium text-sm">{imageFile.name}</div>
                    <div className="text-xs text-gray-500">
                      {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeImage}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <ImageIcon size={32} className="mx-auto text-gray-400 mb-2" />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  className="text-[#7B189F] hover:text-[#671485] font-medium text-sm"
                >
                  Upload Business Image
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  JPG, PNG, GIF up to 5MB
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Business Video */}
        <div>
          <div className="uppercase text-xs font-medium text-gray-500 mb-2">
            BUSINESS VIDEO (OPTIONAL)
          </div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
            <input
              type="file"
              ref={videoInputRef}
              onChange={handleVideoChange}
              className="hidden"
              accept="video/*"
            />

            {videoFile ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Video size={20} className="text-purple-500 mr-3" />
                  <div>
                    <div className="font-medium text-sm">{videoFile.name}</div>
                    <div className="text-xs text-gray-500">
                      {(videoFile.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                </div>
                <button
                  onClick={removeVideo}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <Video size={32} className="mx-auto text-gray-400 mb-2" />
                <button
                  onClick={() => videoInputRef.current?.click()}
                  className="text-[#7B189F] hover:text-[#671485] font-medium text-sm"
                >
                  Upload Business Video
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  MP4, MOV, AVI up to 50MB
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-6 border-t border-[#e1e5e4] mt-6">
        <button
          onClick={() => setSelectedOption(null)}
          className="text-[#232123] bg-transparent hover:bg-transparent font-normal text-base"
        >
          Back
        </button>
        <button
          onClick={handleBusinessDetailsSubmit}
          className="bg-[#7b189f] hover:bg-[#7b189f] text-white px-6 sm:px-8 py-2 rounded-md"
        >
          {isLoading && (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
          {isLoading ? "Saving..." : "Save Details"}{" "}
        </button>
      </div>
    </div>
  );

  const renderCatalogForm = () => (
    <div className="p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
      <h3 className="text-lg font-semibold text-[#232123] mb-4">Add Item</h3>

      {/* Catalog Image Upload Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-[#232123] mb-3">
          Catalog Image
        </label>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
          <input
            type="file"
            ref={catalogImageInputRef}
            onChange={handleCatalogMainImageChange}
            className="hidden"
            accept="image/*"
          />

          {catalogForm.catalogImage ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gray-100 rounded-lg mr-3 overflow-hidden">
                  <img
                    src={
                      URL.createObjectURL(catalogForm.catalogImage) ||
                      "/placeholder.svg"
                    }
                    alt="Catalog"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <div className="font-medium text-sm ">
                    {catalogForm.catalogImage.name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {(catalogForm.catalogImage.size / 1024 / 1024).toFixed(2)}{" "}
                    MB
                  </div>
                </div>
              </div>
              <button
                onClick={removeCatalogImage}
                className="text-red-500 hover:text-red-700"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="text-center">
              <ImageIcon size={32} className="mx-auto text-gray-400 mb-2" />
              <button
                onClick={() => catalogImageInputRef.current?.click()}
                className="text-[#7B189F] hover:text-[#671485] font-medium text-sm"
              >
                Upload Catalog Image
              </button>
              <p className="text-xs text-gray-500 mt-1">
                JPG, PNG, GIF up to 5MB
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add New Item Form */}
      <div className="mb-6 p-4 border border-[#e1e5e4] rounded-lg bg-[#f9f9f9]">
        <h4 className="text-base font-medium text-[#232123] mb-3">
          Add New Item
        </h4>

        <div className="space-y-3">
          <input
            name="name"
            type="text"
            value={newItem.name}
            onChange={handleNewItemChange}
            placeholder="Item name*"
            className="w-full px-3 py-2 border border-[#e1e5e4] rounded-md focus:outline-none focus:ring-1 focus:ring-[#7b189f] text-sm"
          />

          <textarea
            name="description"
            value={newItem.description}
            onChange={handleNewItemChange}
            placeholder="Description (optional)"
            className="w-full px-3 py-2 border border-[#e1e5e4] rounded-md focus:outline-none focus:ring-1 focus:ring-[#7b189f] text-sm min-h-[60px] resize-none"
          />

          <input
            name="price"
            type="text"
            value={newItem.price}
            onChange={handleNewItemChange}
            placeholder="Price (e.g., $10, $50/hour, Free)"
            className="w-full px-3 py-2 border border-[#e1e5e4] rounded-md focus:outline-none focus:ring-1 focus:ring-[#7b189f] text-sm"
          />

          <button
            onClick={addCatalogItem}
            className="w-full bg-[#7b189f] text-white py-2 rounded-md text-sm hover:bg-[#671485] transition-colors"
          >
            Add Item
          </button>
        </div>
      </div>

      {/* Current Items List */}
      {catalogForm.items.length > 0 && (
        <div className="mb-6">
          <h4 className="text-base font-medium text-[#232123] mb-3">
            Current Items ({catalogForm.items.length})
          </h4>
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {catalogForm.items.map((item) => (
              <div
                key={item.id}
                className="flex items-center justify-between p-3 border border-[#e1e5e4] rounded-md bg-white"
              >
                <div className="flex-1">
                  <div className="font-medium text-sm text-[#232123]">
                    {item.name}
                  </div>
                  {item.description && (
                    <div className="text-xs text-[#777280] mt-1">
                      {item.description}
                    </div>
                  )}
                  {item.price && (
                    <div className="text-sm text-[#7b189f] font-medium mt-1">
                      {item.price}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeCatalogItem(item.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between pt-6 border-t border-[#e1e5e4] mt-6">
        <button
          onClick={() => setSelectedOption(null)}
          className="text-[#232123] bg-transparent hover:bg-transparent font-normal text-base"
        >
          Back
        </button>
        <button
          onClick={handleCatalogSubmit}
          className="bg-[#7b189f] hover:bg-[#7b189f] text-white px-6 sm:px-8 py-2 rounded-md"
        >
          {isLoading && (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
          {isLoading ? "Saving..." : "Save Items"}{" "}
        </button>
      </div>
    </div>
  );

  const renderAdditionalInfoForm = () => (
    <div className="p-4 sm:p-6 max-h-[80vh] overflow-y-auto">
      <h3 className="text-lg font-semibold text-[#232123] mb-4">
        Additional Information
      </h3>

      <div className="mb-6">
        <p className="text-sm text-[#777280] mb-4">
          Add any additional information about your business such as business
          hours, website, special notes, or other details.
        </p>

        <textarea
          name="additionalInfo"
          value={additionalInfoForm.additionalInfo}
          onChange={handleAdditionalInfoChange}
          placeholder="Enter additional business information here..."
          className="w-full border border-[#e1e5e4] rounded-md p-4 min-h-[200px] text-sm text-[#232123] placeholder:text-[#9a999a] resize-none focus:outline-none focus:ring-1 focus:ring-[#7b189f]"
        />
      </div>

      <div className="flex justify-between pt-6 border-t border-[#e1e5e4] mt-6">
        <button
          onClick={() => setSelectedOption(null)}
          className="text-[#232123] bg-transparent hover:bg-transparent font-normal text-base"
        >
          Back
        </button>
        <button
          onClick={handleAdditionalInfoSubmit}
          className="bg-[#7b189f] hover:bg-[#7b189f] text-white px-6 sm:px-8 py-2 rounded-md"
        >
          {isLoading && (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          )}
          {isLoading ? "Saving..." : "Save Additional Info"}{" "}
        </button>
      </div>
    </div>
  );

  const renderMainOptions = () => (
    <div className="p-4 sm:p-6">
      <h2 className="text-xl sm:text-[22px] font-bold text-[#232123] mb-6 font-fraunces">
        Edit Business
      </h2>

      <div className="space-y-3">
        {/* Edit Offers Option */}
        <button
          onClick={() => setSelectedOption("offers")}
          className="w-full flex items-center p-4 border border-[#e1e5e4] rounded-lg hover:bg-[#f5f3f5] transition-colors"
        >
          <div className="w-10 h-10 bg-[#e8f5c8] rounded-lg flex items-center justify-center mr-4">
            <Edit3 className="h-5 w-5 text-[#2c3f02]" />
          </div>
          <div className="text-left">
            <h3 className="font-medium text-[#232123] text-base">
              Edit Offers
            </h3>
            <p className="text-sm text-[#777280]">
              Manage promotions and discounts
            </p>
          </div>
        </button>

        {/* Edit Catalog Option */}
        <button
          onClick={handleCatalogEdit}
          className="w-full flex items-center p-4 border border-[#e1e5e4] rounded-lg hover:bg-[#f5f3f5] transition-colors"
        >
          <div className="w-10 h-10 bg-[#f0e6f7] rounded-lg flex items-center justify-center mr-4">
            <Menu className="h-5 w-5 text-[#7b189f]" />
          </div>
          <div className="text-left">
            <h3 className="font-medium text-[#232123] text-base">Add Item</h3>
            <p className="text-sm text-[#777280]">Add items to your catalog</p>
          </div>
        </button>

        {/* Edit Business Details Option */}
        <button
          onClick={handleBusinessDetailsEdit}
          className="w-full flex items-center p-4 border border-[#e1e5e4] rounded-lg hover:bg-[#f5f3f5] transition-colors"
        >
          <div className="w-10 h-10 bg-[#fef3e2] rounded-lg flex items-center justify-center mr-4">
            <Settings className="h-5 w-5 text-[#d6722d]" />
          </div>
          <div className="text-left">
            <h3 className="font-medium text-[#232123] text-base">
              Edit Business Details
            </h3>
            <p className="text-sm text-[#777280]">
              Update contact info and media
            </p>
          </div>
        </button>

        <button
          onClick={handleAdditionalInfoEdit}
          className="w-full flex items-center p-4 border border-[#e1e5e4] rounded-lg hover:bg-[#f5f3f5] transition-colors"
        >
          <div className="w-10 h-10 bg-[#e6f3ff] rounded-lg flex items-center justify-center mr-4">
            <FileText className="h-5 w-5 text-[#0066cc]" />
          </div>
          <div className="text-left">
            <h3 className="font-medium text-[#232123] text-base">
              Additional Information
            </h3>
            <p className="text-sm text-[#777280]">
              Business hours, website, and other details
            </p>
          </div>
        </button>
      </div>
    </div>
  );

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-[500px] relative shadow-xl mx-auto my-auto">
        <button
          onClick={() => {
            setOpen(false);
            setSelectedOption(null);
          }}
          className="absolute right-3 top-3 sm:right-4 sm:top-4 text-[#232123] hover:text-[#232123]/80 p-1 touch-manipulation z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {selectedOption === "offers" && renderOfferForm()}
        {selectedOption === "businessDetails" && renderBusinessDetailsForm()}
        {selectedOption === "catalog" && renderCatalogForm()}
        {selectedOption === "additionalInfo" && renderAdditionalInfoForm()}
        {!selectedOption && renderMainOptions()}
      </div>
    </div>
  );
}
